/* tl-stockists.js : the Terralite store locator (sections/tl-stockists.liquid).
   Renders the list from the section's JSON, runs the filter, the place search (Mapbox Geocoding v6), the browser
   geolocation sort, and the map (Leaflet 1.9.4 loaded on demand; tiles from Mapbox or any tile URL). No other
   dependency. Brief: _Redesign/Docs/STOCKIST-NATIVE.md. */
(function () {
  'use strict';
  var LEAFLET_JS = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.js';
  var LEAFLET_SRI = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
  var MAPBOX_TILES = 'https://api.mapbox.com/styles/v1/{id}/tiles/512/{z}/{x}/{y}{r}?access_token={accessToken}';
  var MAPBOX_ATTR = '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> <a href="https://www.mapbox.com/map-feedback/" target="_blank" rel="noopener">Improve this map</a>';
  var GEOCODE = 'https://api.mapbox.com/search/geocode/v6/forward';
  var leafletLoad = null;
  var instances = {};

  function loadLeaflet() {
    if (window.L && window.L.map) return Promise.resolve(window.L);
    if (leafletLoad) return leafletLoad;
    leafletLoad = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = LEAFLET_JS; s.integrity = LEAFLET_SRI; s.crossOrigin = ''; s.async = true;
      s.onload = function () { resolve(window.L); };
      s.onerror = function () { leafletLoad = null; reject(new Error('Leaflet failed to load')); };
      document.head.appendChild(s);
    });
    return leafletLoad;
  }
  function esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function fmt(tpl, vars) {
    return String(tpl || '').replace(/\{(\w+)\}/g, function (_, k) { return vars[k] != null ? vars[k] : ''; });
  }
  function haversineKm(a, b) {
    var R = 6371, dLat = (b.lat - a.lat) * Math.PI / 180, dLng = (b.lng - a.lng) * Math.PI / 180;
    var x = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  }
  function readJSON(el) { try { return JSON.parse(el.textContent); } catch (e) { return null; } }
  function hasCoords(s) { return typeof s.lat === 'number' && typeof s.lng === 'number' && !isNaN(s.lat) && !isNaN(s.lng); }
  function cityLine(s) {
    var parts = [];
    if (s.city) parts.push(s.city);
    var tail = [s.rc || s.region, s.postal].filter(Boolean).join(' ');
    if (tail) parts.push(tail);
    return parts.join(', ');
  }
  function webHref(u) { if (!u) return ''; return /^https?:\/\//i.test(u) ? u : 'https://' + u; }
  var DIAL = { GB: '44', AU: '61', SE: '46', CH: '41', BH: '973', DE: '49', FR: '33', NL: '31', NZ: '64', JP: '81', MX: '52', IE: '353', ES: '34', IT: '39', DK: '45', NO: '47', AE: '971', SG: '65', HK: '852' };
  /* the phone as search results print it: (928) 778-2095 for North America, +41 76 415 88 11 style elsewhere */
  function fmtPhone(raw, cc) {
    if (!raw) return '';
    var s = String(raw).trim(), d = s.replace(/\D/g, '');
    var nanp = cc === 'US' || cc === 'CA' || cc === 'BS' || (!/^\+/.test(s) && (d.length === 10 || (d.length === 11 && d.charAt(0) === '1')));
    if (nanp) {
      if (d.length === 11 && d.charAt(0) === '1') d = d.slice(1);
      return d.length === 10 ? '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6) : s;
    }
    if (/^\+/.test(s)) return s.replace(/\s+/g, ' ');
    var code = DIAL[cc];
    if (code && d.indexOf(code) === 0) {
      var rest = d.slice(code.length), groups = [];
      while (rest.length > 4) { groups.push(rest.slice(0, rest.length % 3 === 1 ? 2 : 3)); rest = rest.slice(groups[groups.length - 1].length); }
      groups.push(rest);
      return '+' + code + ' ' + groups.join(' ');
    }
    return s;
  }

  function init(root) {
    var id = root.getAttribute('data-section-id');
    if (instances[id]) destroy(id);
    var data = readJSON(root.querySelector('[data-stockists]')) || [];
    var cfg = readJSON(root.querySelector('[data-config]')) || {};
    var L10n = cfg.labels || {};
    var els = {
      controls: root.querySelector('[data-controls]'), q: root.querySelector('[data-q]'), search: root.querySelector('[data-search]'),
      locate: root.querySelector('[data-locate]'), filter: root.querySelector('[data-filter]'), clear: root.querySelector('[data-clear]'),
      map: root.querySelector('[data-map]'), status: root.querySelector('[data-status]'), list: root.querySelector('[data-list]')
    };
    var state = { mode: 'all', filter: '', origin: null, originLabel: '', unit: 'mi', active: null, shown: Math.max(1, Number(cfg.results) || 10) };
    var inst = { root: root, map: null, markers: {}, originMarker: null, listeners: [] };
    instances[id] = inst;

    data.forEach(function (s, i) { s._i = i; s.sort = Number(s.sort) || 0; s.lat = s.lat == null ? null : Number(s.lat); s.lng = s.lng == null ? null : Number(s.lng); });
    var countries = {};
    data.forEach(function (s) {
      var cc = s.cc || '??';
      countries[cc] = countries[cc] || { cc: cc, name: s.country || cc, regions: {}, items: [] };
      countries[cc].items.push(s);
      var rk = s.region || s.rc || '';
      countries[cc].regions[rk] = countries[cc].regions[rk] || { key: rk, name: s.region || s.rc || '', items: [] };
      countries[cc].regions[rk].items.push(s);
    });
    function countryOrder(a, b) { if (a.cc === 'US') return -1; if (b.cc === 'US') return 1; return a.name.localeCompare(b.name); }
    var countryList = Object.keys(countries).map(function (k) { return countries[k]; }).sort(countryOrder);
    var byName = function (a, b) { return (a.sort - b.sort) || a.name.localeCompare(b.name); };
    var ccParam = Object.keys(countries).filter(function (c) { return c && c !== '??'; }).map(function (c) { return c.toLowerCase(); }).join(',');

    /* filter options */
    if (els.filter) {
      countryList.forEach(function (c) {
        var regions = Object.keys(c.regions).map(function (k) { return c.regions[k]; }).filter(function (r) { return r.name; }).sort(function (a, b) { return a.name.localeCompare(b.name); });
        if (c.cc === 'US' && regions.length > 1) {
          var og = document.createElement('optgroup'); og.label = L10n.usFirst || c.name;
          og.appendChild(new Option((L10n.filterAllIn || 'All ') + (L10n.usFirst || c.name), 'cc:US'));
          regions.forEach(function (r) { og.appendChild(new Option(r.name, 'rc:US:' + r.key)); });
          els.filter.appendChild(og);
        }
      });
      var others = countryList.filter(function (c) { return c.cc !== 'US'; });
      if (others.length) {
        var og2 = document.createElement('optgroup'); og2.label = L10n.worldwide || 'Worldwide';
        others.forEach(function (c) { og2.appendChild(new Option(c.name, 'cc:' + c.cc)); });
        els.filter.appendChild(og2);
      }
    }

    function visible() {
      var items = data.slice();
      if (state.mode === 'filter' && state.filter) {
        var p = state.filter.split(':');
        items = items.filter(function (s) { return p[0] === 'cc' ? s.cc === p[1] : (s.cc === p[1] && (s.region || s.rc || '') === p.slice(2).join(':')); });
      }
      if (state.origin) {
        items.forEach(function (s) { s._d = hasCoords(s) ? haversineKm(state.origin, s) : Infinity; });
        items.sort(function (a, b) { return a._d - b._d || byName(a, b); });
      }
      return items;
    }
    function unitFor(items) {
      if (cfg.unit === 'mi' || cfg.unit === 'km') return cfg.unit;
      var nearest = items.filter(hasCoords)[0];
      return nearest && nearest.cc === 'US' ? 'mi' : 'km';
    }
    function distText(km) {
      if (!isFinite(km)) return '';
      var v = state.unit === 'mi' ? km * 0.621371 : km;
      return (v < 10 ? v.toFixed(1) : Math.round(v)) + ' ' + (L10n[state.unit] || state.unit);
    }
    function card(s) {
      var h = '<button type="button" class="tl-stk-card' + (state.active === s.id ? ' is-active' : '') + '" data-id="' + esc(s.id) + '">';
      h += '<span class="tl-stk-name">' + esc(s.name) + (state.origin && isFinite(s._d) ? '<span class="tl-stk-dist">' + esc(distText(s._d)) + '</span>' : '') + '</span>';
      if (s.a1) h += '<span class="tl-stk-line">' + esc(s.a1) + '</span>';
      if (s.a2) h += '<span class="tl-stk-line">' + esc(s.a2) + '</span>';
      var cl = cityLine(s); if (cl) h += '<span class="tl-stk-line">' + esc(cl) + '</span>';
      if (state.origin || state.mode === 'all' && !cfg.groupCountries) { if (s.country && s.cc !== 'US') h += '<span class="tl-stk-line">' + esc(s.country) + '</span>'; }
      var meta = metaHtml(s);
      if (meta) h += '<span class="tl-stk-meta">' + meta + '</span>';
      return h + '</button>';
    }
    function metaHtml(s) {
      var m = ''; /* website first, then the phone, each on its own line (Justin, 2026-09-28) */
      if (cfg.showWebsite && s.web) m += '<a class="tl-stk-link" href="' + esc(webHref(s.web)) + '" target="_blank" rel="noopener">' + esc(L10n.website || 'Website') + '</a>';
      if (cfg.showPhone && s.phone) m += '<span class="tl-stk-phone">' + esc(fmtPhone(s.phone, s.cc)) + '</span>';
      return m;
    }
    function renderList() {
      var items = visible();
      var idle = state.mode === 'all' && !state.origin;
      if (idle && cfg.initialList === 'none') { els.list.innerHTML = L10n.prompt ? '<p class="tl-stk-prompt">' + esc(L10n.prompt) + '</p>' : ''; return items; }
      var listed = idle && cfg.initialList === 'US' ? items.filter(function (s) { return s.cc === 'US'; }) : items;
      if (!listed.length) { els.list.innerHTML = '<p class="tl-stk-empty">' + esc(L10n.empty || '') + '</p>'; return items; }
      var h = '';
      if (state.origin) {
        var shown = listed.slice(0, state.shown);
        h = '<div class="tl-stk-grid">' + shown.map(card).join('') + '</div>';
        if (listed.length > shown.length) h += '<div class="tl-stk-more-wrap"><button type="button" class="tl-stk-btn tl-stk-btn--ghost tl-stk-more" data-more>' + esc(L10n.more || 'Show more') + '</button></div>';
      } else if (!cfg.groupCountries) {
        h = '<div class="tl-stk-grid">' + listed.map(card).join('') + '</div>';
      } else {
        items = listed;
        var groups = {};
        items.forEach(function (s) { var cc = s.cc || '??'; groups[cc] = groups[cc] || { cc: cc, name: s.country || cc, regions: {} }; var rk = s.region || s.rc || ''; groups[cc].regions[rk] = groups[cc].regions[rk] || { name: s.region || s.rc || '', items: [] }; groups[cc].regions[rk].items.push(s); });
        var gl = Object.keys(groups).map(function (k) { return groups[k]; }).sort(countryOrder);
        var us = gl.filter(function (g) { return g.cc === 'US'; }), rest = gl.filter(function (g) { return g.cc !== 'US'; });
        function block(title, regions) {
          var out = '<div class="tl-stk-country-block"><h3 class="tl-stk-country">' + esc(title) + '</h3><div class="tl-stk-groups">';
          regions.forEach(function (r) {
            out += '<div class="tl-stk-group">' + (r.name ? '<h4 class="tl-stk-region">' + esc(r.name) + '</h4>' : '') + '<div class="tl-stk-grid">' + r.items.sort(byName).map(card).join('') + '</div></div>';
          });
          return out + '</div></div>';
        }
        us.forEach(function (g) { h += block(L10n.usFirst || g.name, Object.keys(g.regions).sort().map(function (k) { return g.regions[k]; })); });
        if (rest.length) {
          var regions = [];
          rest.forEach(function (g) { var all = []; Object.keys(g.regions).forEach(function (k) { all = all.concat(g.regions[k].items); }); regions.push({ name: g.name, items: all }); });
          h += block(rest.length === 1 && state.mode === 'filter' ? rest[0].name : (L10n.worldwide || 'Worldwide'), regions);
        }
      }
      els.list.innerHTML = h;
      return items;
    }
    function setStatus(text) { if (els.status) els.status.textContent = text || ''; }
    function statusFor(items) {
      if (state.mode === 'all' && !state.origin && cfg.initialList === 'none') return '';
      if (state.origin) return fmt(L10n.statusNear, { place: state.originLabel, count: items.length, shown: Math.min(items.length, state.shown) });
      if (state.mode === 'filter' && state.filter) {
        var opt = els.filter && els.filter.options[els.filter.selectedIndex];
        return fmt(L10n.statusFilter, { count: items.length, place: opt ? opt.text.replace(/^All /, '') : '' });
      }
      return fmt(L10n.statusAll, { count: data.length, countries: countryList.filter(function (c) { return c.cc !== '??'; }).length });
    }
    function refresh(fitMode) {
      var items = renderList();
      setStatus(statusFor(items));
      root.classList.toggle('has-origin', !!state.origin);
      root.classList.toggle('has-filter', state.mode === 'filter' && !!state.filter);
      syncMarkers(items, fitMode);
      return items;
    }

    /* map */
    function tileLayer(L) {
      if (cfg.tileUrl) return L.tileLayer(cfg.tileUrl, { attribution: cfg.tileAttribution || '', maxZoom: 19 });
      return L.tileLayer(MAPBOX_TILES, { id: cfg.styleId || 'mapbox/light-v11', accessToken: cfg.token, tileSize: 512, zoomOffset: -1, maxZoom: 19, attribution: MAPBOX_ATTR });
    }
    function setupMap(L) {
      if (!els.map || inst.map) return;
      var mobile = L.Browser.mobile;
      var map = L.map(els.map, { scrollWheelZoom: !!cfg.scrollZoom, dragging: mobile ? !!cfg.mobileDrag : true, tap: mobile ? !!cfg.mobileDrag : true, worldCopyJump: true, zoomControl: true, attributionControl: true, minZoom: 1 });
      tileLayer(L).addTo(map);
      inst.map = map;
      data.forEach(function (s) {
        if (!hasCoords(s)) return;
        var icon = L.divIcon({ className: '', html: '<div class="tl-stk-pin" data-pin="' + esc(s.id) + '"></div>', iconSize: [14, 14], iconAnchor: [7, 7], popupAnchor: [0, -13] });
        var m = L.marker([s.lat, s.lng], { icon: icon, title: s.name, keyboard: true, alt: s.name });
        var pop = '<span class="tl-stk-pop-name">' + esc(s.name) + '</span>' + [s.a1, s.a2, cityLine(s), s.cc !== 'US' ? s.country : ''].filter(Boolean).map(esc).join('<br>');
        var pm = metaHtml(s); if (pm) pop += '<span class="tl-stk-pop-meta">' + pm + '</span>';
        m.bindPopup(pop, { closeButton: true, autoPanPadding: [30, 30], className: 'tl-stk-popup', minWidth: 200, maxWidth: 320 });
        m.on('click', function () { select(s.id, 'map'); });
        m.addTo(map);
        inst.markers[s.id] = m;
      });
      syncMarkers(visible(), 'start');
    }
    function fitTo(items, opts) {
      var map = inst.map; if (!map) return;
      var pts = items.filter(hasCoords).map(function (s) { return [s.lat, s.lng]; });
      if (!pts.length) return;
      if (pts.length === 1) { map.setView(pts[0], opts && opts.zoom || 12); return; }
      map.fitBounds(pts, { padding: [36, 36], maxZoom: (opts && opts.maxZoom) || 12 });
    }
    function syncMarkers(items, fitMode) {
      var map = inst.map; if (!map || !window.L) return;
      var show = {}; items.forEach(function (s) { show[s.id] = true; });
      Object.keys(inst.markers).forEach(function (k) { var m = inst.markers[k]; if (show[k]) { if (!map.hasLayer(m)) m.addTo(map); } else if (map.hasLayer(m)) map.removeLayer(m); });
      if (inst.originMarker) { map.removeLayer(inst.originMarker); inst.originMarker = null; }
      if (state.origin) {
        inst.originMarker = window.L.marker([state.origin.lat, state.origin.lng], { icon: window.L.divIcon({ className: '', html: '<div class="tl-stk-origin"></div>', iconSize: [18, 18], iconAnchor: [9, 9] }), interactive: false, zIndexOffset: -100 }).addTo(map);
        var near = items.filter(hasCoords).slice(0, Math.max(1, cfg.nearest || 6));
        fitTo(near.concat([{ lat: state.origin.lat, lng: state.origin.lng }]), { maxZoom: 11 });
      } else if (fitMode === 'start' && cfg.start === 'US') {
        fitTo(items.filter(function (s) { return s.cc === 'US'; }).length ? items.filter(function (s) { return s.cc === 'US'; }) : items, { maxZoom: 5 });
      } else if (fitMode) {
        fitTo(items, { maxZoom: state.mode === 'filter' ? 10 : 5 });
      }
    }
    function select(sid, from) {
      state.active = sid;
      root.querySelectorAll('.tl-stk-card').forEach(function (c) { c.classList.toggle('is-active', c.getAttribute('data-id') === sid); });
      root.querySelectorAll('.tl-stk-pin').forEach(function (p) { p.classList.toggle('is-active', p.getAttribute('data-pin') === sid); });
      var m = inst.markers[sid];
      if (m) m.setZIndexOffset(1000);
      Object.keys(inst.markers).forEach(function (k) { if (k !== sid) inst.markers[k].setZIndexOffset(0); });
      if (from === 'map') {
        return; /* the popup is the answer; the page stays where it is */
      } else if (m && inst.map) {
        if (!inst.map.hasLayer(m)) m.addTo(inst.map);
        var z = Math.max(inst.map.getZoom(), 12);
        inst.map.flyTo(m.getLatLng(), z, { duration: .8 });
        inst.map.once('moveend', function () { m.openPopup(); });
        if (els.map && els.map.getBoundingClientRect().bottom < 0) els.map.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }

    /* origin: a search result or the visitor's position */
    function setOrigin(latlng, label) {
      state.origin = latlng; state.originLabel = label; state.mode = 'near'; state.filter = ''; state.shown = Math.max(1, Number(cfg.results) || 10);
      if (els.filter) els.filter.value = '';
      state.unit = unitFor(visible());
      refresh(true);
    }
    function clearAll() {
      state.origin = null; state.originLabel = ''; state.mode = 'all'; state.filter = ''; state.active = null;
      if (els.filter) els.filter.value = '';
      if (els.q) els.q.value = '';
      refresh(true);
      if (inst.map) inst.map.closePopup();
    }
    function geocodeList(q, opts) {
      var zip5 = /^\d{5}(-\d{4})?$/.test(q.trim());
      var params = 'q=' + encodeURIComponent(q.trim()) + '&access_token=' + encodeURIComponent(cfg.token) + '&limit=' + (opts.limit || 1) + '&language=en&autocomplete=' + (opts.autocomplete ? 'true' : 'false') +
        (ccParam ? '&country=' + encodeURIComponent(zip5 ? 'us' : ccParam) : '') +
        '&types=' + (zip5 ? 'postcode' : 'postcode,place,locality,district,region,address');
      return fetch(GEOCODE + '?' + params).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); }).then(function (j) {
        return ((j && j.features) || []).map(function (f) {
          var c = f.geometry.coordinates, p = f.properties || {}, place = p.place_formatted || '';
          var short = p.feature_type === 'postcode' && p.name && place ? p.name + ', ' + place.split(',')[0] : (p.name || p.full_address || q);
          return { lat: c[1], lng: c[0], label: short, name: p.name || short, place: place };
        });
      });
    }
    function geocode(q) { return geocodeList(q, { limit: 1, autocomplete: false }).then(function (l) { return l[0] || null; }); }
    /* suggestions under the search box (Mapbox, autocomplete) so the visitor confirms the place before the list moves */
    var sugg = { items: [], index: -1, timer: null, box: null, seq: 0 };
    function suggestBox() {
      if (!sugg.box) {
        sugg.box = document.createElement('ul'); sugg.box.className = 'tl-stk-suggest'; sugg.box.setAttribute('role', 'listbox'); sugg.box.hidden = true;
        els.q.parentNode.appendChild(sugg.box); els.q.setAttribute('aria-autocomplete', 'list'); els.q.setAttribute('aria-expanded', 'false');
        on(sugg.box, 'mousedown', function (e) { e.preventDefault(); });
        on(sugg.box, 'click', function (e) { var li = e.target.closest('li[data-i]'); if (li) pickSuggestion(sugg.items[Number(li.getAttribute('data-i'))]); });
      }
      return sugg.box;
    }
    function hideSuggestions() { if (sugg.box) { sugg.box.hidden = true; sugg.box.innerHTML = ''; } sugg.items = []; sugg.index = -1; if (els.q) els.q.setAttribute('aria-expanded', 'false'); }
    function showSuggestions(items) {
      var box = suggestBox(); sugg.items = items; sugg.index = -1;
      if (!items.length) { hideSuggestions(); return; }
      box.innerHTML = items.map(function (it, i) { return '<li role="option" data-i="' + i + '"><b>' + esc(it.name) + '</b>' + (it.place ? '<span>' + esc(it.place) + '</span>' : '') + '</li>'; }).join('');
      box.hidden = false; els.q.setAttribute('aria-expanded', 'true');
    }
    function moveSuggestion(d) {
      if (!sugg.items.length) return;
      sugg.index = (sugg.index + d + sugg.items.length) % sugg.items.length;
      [].forEach.call(sugg.box.children, function (li, i) { li.classList.toggle('is-active', i === sugg.index); });
    }
    function pickSuggestion(it) { if (!it) return; hideSuggestions(); els.q.value = it.label; setOrigin({ lat: it.lat, lng: it.lng }, it.label); }
    function onInput() {
      if (!cfg.token) return;
      var q = els.q.value.trim();
      clearTimeout(sugg.timer);
      if (q.length < 3) { hideSuggestions(); return; }
      sugg.timer = setTimeout(function () {
        var seq = ++sugg.seq;
        geocodeList(q, { limit: 5, autocomplete: true }).then(function (items) { if (seq === sugg.seq && els.q.value.trim() === q) showSuggestions(items); }).catch(function () { hideSuggestions(); });
      }, 250);
    }
    function onKey(e) {
      if (!sugg.items.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); moveSuggestion(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); moveSuggestion(-1); }
      else if (e.key === 'Escape') { hideSuggestions(); }
      else if (e.key === 'Enter' && sugg.index >= 0) { e.preventDefault(); pickSuggestion(sugg.items[sugg.index]); }
    }
    function onSearch(e) {
      if (e) e.preventDefault();
      if (!els.q || !cfg.token) return;
      var q = els.q.value; if (!q.trim()) return;
      if (sugg.items.length) { pickSuggestion(sugg.items[sugg.index >= 0 ? sugg.index : 0]); return; }
      clearTimeout(sugg.timer); hideSuggestions();
      els.search.classList.add('is-busy'); setStatus(L10n.searching || '');
      geocode(q).then(function (r) {
        els.search.classList.remove('is-busy');
        if (!r) { setStatus(L10n.notFound || ''); return; }
        setOrigin({ lat: r.lat, lng: r.lng }, r.label);
      }).catch(function () { els.search.classList.remove('is-busy'); setStatus(L10n.searchDown || ''); });
    }
    function onLocate() {
      if (!navigator.geolocation) { setStatus(L10n.noLocation || ''); return; }
      els.locate.classList.add('is-busy'); setStatus(L10n.locating || '');
      navigator.geolocation.getCurrentPosition(function (pos) {
        els.locate.classList.remove('is-busy');
        setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude }, L10n.you || 'your location');
      }, function () { els.locate.classList.remove('is-busy'); setStatus(L10n.noLocation || ''); }, { enableHighAccuracy: false, timeout: 12000, maximumAge: 300000 });
    }
    function onFilter() {
      state.origin = null; state.originLabel = ''; state.filter = els.filter.value; state.mode = state.filter ? 'filter' : 'all';
      if (els.q) els.q.value = '';
      refresh(true);
    }
    function onListClick(e) {
      if (e.target.closest('[data-more]')) { state.shown += Math.max(1, Number(cfg.results) || 10); renderList(); return; }
      var c = e.target.closest('.tl-stk-card'); if (!c || e.target.closest('a')) return; select(c.getAttribute('data-id'), 'list');
    }
    function on(el, ev, fn) { if (!el) return; el.addEventListener(ev, fn); inst.listeners.push([el, ev, fn]); }
    on(els.controls, 'submit', onSearch);
    on(els.q, 'input', onInput);
    on(els.q, 'keydown', onKey);
    on(els.q, 'blur', function () { setTimeout(hideSuggestions, 150); });
    on(els.locate, 'click', onLocate);
    on(els.filter, 'change', onFilter);
    on(els.clear, 'click', clearAll);
    on(els.list, 'click', onListClick);

    refresh(false);
    if (els.map && (cfg.token || cfg.tileUrl)) {
      loadLeaflet().then(setupMap).catch(function () { els.map.classList.add('is-off'); });
    } else if (els.map) { els.map.classList.add('is-off'); }
  }
  function destroy(id) {
    var inst = instances[id]; if (!inst) return;
    inst.listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2]); });
    if (inst.map) { try { inst.map.remove(); } catch (e) {} }
    delete instances[id];
  }
  function boot() { document.querySelectorAll('[data-section-type="tl-stockists"]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', function (e) { var r = e.target && e.target.querySelector && e.target.querySelector('[data-section-type="tl-stockists"]'); if (r) init(r); });
  document.addEventListener('shopify:section:unload', function (e) { if (e.detail && e.detail.sectionId) destroy(e.detail.sectionId); });
})();
